import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { parseSecureViewerParams } from "../../utils/secureFileHelper";
import { Loader } from "../../components/Loader";

/**
 * Secure PDF Viewer
 *
 * Renders a PDF file with protections against:
 * - Screenshots (CSS overlay, user-select disabled)
 * - Downloading (right-click disabled, keyboard shortcuts blocked)
 * - Unauthorized access (redirects to login if not authenticated)
 *
 * If the user is not logged in, they are redirected to login
 * and sent back here after successful authentication.
 */
const SecurePDFViewer = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { type, fileUrl } = parseSecureViewerParams(searchParams);
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking
  const [loadError, setLoadError] = useState(false);
  const viewerRef = useRef(null);
  const overlayRef = useRef(null);

  // ─── Auth Check ───────────────────────────────────────────────
  useEffect(() => {
    const userToken = localStorage.getItem("token");
    const adminToken = localStorage.getItem("adminAuth");

    if (userToken || adminToken) {
      // User is authenticated (either as a regular user or admin)
      setIsAuthenticated(true);
    } else {
      // Not logged in → redirect to admin login with return URL
      const currentUrl = window.location.pathname + window.location.search;
      navigate(`/admin/login/?redirect=${encodeURIComponent(currentUrl)}`, {
        replace: true,
      });
    }
  }, [navigate]);

  // ─── Anti-Download: Block keyboard shortcuts ─────────────────
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleKeyDown = (e) => {
      // Block Ctrl+S (Save), Ctrl+P (Print), Ctrl+Shift+I (DevTools)
      const isCtrl = e.ctrlKey || e.metaKey;
      if (isCtrl) {
        const blockedKeys = ["s", "p", "S", "P", "i", "I", "j", "J", "u", "U"];
        if (blockedKeys.includes(e.key)) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      }
      // Block F12 (DevTools)
      if (e.key === "F12") {
        e.preventDefault();
        return false;
      }
      // Block PrintScreen
      if (e.key === "PrintScreen") {
        e.preventDefault();
        return false;
      }
    };

    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    const handleCopy = (e) => {
      e.preventDefault();
      return false;
    };

    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Attach listeners at document level for maximum coverage
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("contextmenu", handleContextMenu, true);
    document.addEventListener("copy", handleCopy, true);
    document.addEventListener("cut", handleCopy, true);
    document.addEventListener("dragstart", handleDragStart, true);

    // Also attach to the overlay for double protection
    const overlay = overlayRef.current;
    if (overlay) {
      overlay.addEventListener("contextmenu", handleContextMenu);
      overlay.addEventListener("copy", handleCopy);
      overlay.addEventListener("dragstart", handleDragStart);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("contextmenu", handleContextMenu, true);
      document.removeEventListener("copy", handleCopy, true);
      document.removeEventListener("cut", handleCopy, true);
      document.removeEventListener("dragstart", handleDragStart, true);

      if (overlay) {
        overlay.removeEventListener("contextmenu", handleContextMenu);
        overlay.removeEventListener("copy", handleCopy);
        overlay.removeEventListener("dragstart", handleDragStart);
      }
    };
  }, [isAuthenticated]);



  // ─── Loading state ────────────────────────────────────────────
  if (isAuthenticated === null) {
    return (
      <div style={styles.loadingContainer}>
        <Loader />
      </div>
    );
  }

  // ─── Error: No file URL ───────────────────────────────────────
  if (!fileUrl) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorCard}>
          <span style={styles.errorIcon}>⚠️</span>
          <h2 style={styles.errorTitle}>Invalid or Missing File</h2>
          <p style={styles.errorText}>
            The file you are trying to view could not be found or the link is
            invalid. Please check the URL and try again.
          </p>
          <button
            style={styles.backButton}
            onClick={() => navigate("/")}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // ─── Error: Load failed ───────────────────────────────────────
  if (loadError) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorCard}>
          <span style={styles.errorIcon}>❌</span>
          <h2 style={styles.errorTitle}>Failed to Load File</h2>
          <p style={styles.errorText}>
            The file could not be loaded. It may have been removed or you may
            not have permission to access it.
          </p>
          <button
            style={styles.backButton}
            onClick={() => navigate("/")}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // ─── Render the protected PDF viewer ──────────────────────────
  return (
    <div style={styles.container}>
      {/* Top Bar */}
      <div style={styles.topBar}>
        <div style={styles.topBarInner}>
          <span style={styles.brand}>EquipMedy</span>
          <span style={styles.separator}>|</span>
          <span style={styles.badge}>
            <span style={styles.lockIcon}>🔒</span> Protected Document
          </span>
        </div>
      </div>

      {/* Viewer Area */}
      <div style={styles.viewerWrapper} ref={viewerRef}>
        {/* The actual PDF embed — toolbar hidden */}
        <embed
          src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
          type="application/pdf"
          style={styles.pdfEmbed}
          title="Secure Document Viewer"
        />

        {/* Invisible overlay to block right-click "Save As" on PDF */}
        <div
          ref={overlayRef}
          style={styles.protectionOverlay}
        />

        {/* Anti-screenshot watermark overlay (very subtle) */}
        <div style={styles.watermark} />
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <p style={styles.footerText}>
          This document is protected. Screenshots, printing, and downloading
          are disabled for security purposes.
        </p>
      </div>
    </div>
  );
};

// ─── Inline Styles ──────────────────────────────────────────────
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    width: "100vw",
    overflow: "hidden",
    backgroundColor: "#1a1a1a",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    userSelect: "none",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    msUserSelect: "none",
    WebkitUserDrag: "none",
    WebkitTouchCallout: "none",
  },

  topBar: {
    backgroundColor: "#0d0d0d",
    borderBottom: "1px solid #333",
    padding: "12px 24px",
    flexShrink: 0,
  },
  topBarInner: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  brand: {
    color: "#219d8e",
    fontWeight: 700,
    fontSize: "18px",
    letterSpacing: "0.5px",
  },
  separator: {
    color: "#555",
    fontSize: "18px",
  },
  badge: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "#aaa",
    fontSize: "13px",
  },
  lockIcon: {
    fontSize: "14px",
  },

  viewerWrapper: {
    flex: 1,
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#2a2a2a",
  },

  pdfEmbed: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    border: "none",
    outline: "none",
    // Prevent the embed from being draggable
    pointerEvents: "auto",
  },

  protectionOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    // Fully transparent but captures mouse events
    // This blocks right-click "Save As" on the PDF
    zIndex: 1,
    // Don't block clicks on the PDF itself (needed for scrolling)
    pointerEvents: "none",
  },

  watermark: {
    position: "absolute",
    bottom: "40px",
    right: "20px",
    width: "140px",
    height: "24px",
    backgroundImage:
      "repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.02) 4px, rgba(255,255,255,0.02) 8px)",
    zIndex: 2,
    pointerEvents: "none",
  },

  footer: {
    backgroundColor: "#0d0d0d",
    borderTop: "1px solid #333",
    padding: "10px 24px",
    textAlign: "center",
    flexShrink: 0,
  },
  footerText: {
    color: "#666",
    fontSize: "12px",
    margin: 0,
  },

  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    width: "100vw",
    backgroundColor: "#1a1a1a",
  },

  errorContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    width: "100vw",
    backgroundColor: "#1a1a1a",
  },
  errorCard: {
    backgroundColor: "#2a2a2a",
    border: "1px solid #444",
    borderRadius: "12px",
    padding: "48px",
    textAlign: "center",
    maxWidth: "460px",
  },
  errorIcon: {
    fontSize: "48px",
    display: "block",
    marginBottom: "16px",
  },
  errorTitle: {
    color: "#fff",
    fontSize: "22px",
    marginBottom: "12px",
  },
  errorText: {
    color: "#999",
    fontSize: "14px",
    lineHeight: 1.6,
    marginBottom: "24px",
  },
  backButton: {
    backgroundColor: "#219d8e",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "12px 32px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
};

export default SecurePDFViewer;
